import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { SfDataService } from '../../services/sf-data.service';

interface HelpfulContact {
  category: string;
  contactName: string;
  email: string;
}

interface CampaignContact {
  campaignName: string;
  backgroundCheck: string;
  status: string;
  campaignManagerName: string;
  campaignManagerEmail: string;
  commissionsAnalystName: string;
  commissionsAnalyst1Email: string;
}

@Component({
  selector: 'app-sf-help-information',
  templateUrl: './sf-help-information.component.html',
  styleUrls: ['./sf-help-information.component.scss'],
  standalone: false
})
export class SfHelpInformationComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  @ViewChild('contactsTable', { read: DataTableDirective }) contactsDt: DataTableDirective;
  @ViewChild('campaignTable', { read: DataTableDirective }) campaignDt: DataTableDirective;
  @ViewChild('commissionTable', { read: DataTableDirective }) commissionDt: DataTableDirective;

  helpfulContacts: HelpfulContact[] = [];
  campaignPoc: CampaignContact[] = [];
  commissionAnalystPoc: CampaignContact[] = [];

  contactCategories: string[] = [];
  campaignNames: string[] = [];

  contactsReady = false;
  campaignReady = false;

  dtOptionsContacts: any = {};
  dtOptionsCampaign: any = {};
  dtOptionsCommission: any = {};

  constructor(private sfDataService: SfDataService) { }

  ngOnInit(): void {
    this.loadHelpfulContacts();
    this.loadCampaignContacts();
  }

  private loadHelpfulContacts(): void {
    this.sfDataService.getHelpfulContacts()
      .pipe(takeUntil(this.destroy$), catchError(() => of([])))
      .subscribe((contacts: HelpfulContact[]) => {
        this.helpfulContacts = contacts ?? [];
        this.contactCategories = this.distinct(this.helpfulContacts.map(c => c.category));
        this.dtOptionsContacts = this.buildOptions(this.helpfulContacts, [
          { title: 'Contact Category', data: 'category' },
          { title: 'Contact Name', data: 'contactName' },
          { title: 'Contact Email', data: 'email', render: (d: string) => this.mailto(d) }
        ]);
        this.contactsReady = true;
      });
  }

  private loadCampaignContacts(): void {
    this.sfDataService.getCampaignContacts()
      .pipe(takeUntil(this.destroy$), catchError(() => of([])))
      .subscribe((resp: CampaignContact[]) => {
        const rows = (resp ?? []).slice().sort((a, b) =>
          (a.campaignName ?? '').localeCompare(b.campaignName ?? '')
        );
        this.campaignPoc = rows;
        this.commissionAnalystPoc = rows;
        this.campaignNames = this.distinct(rows.map(r => r.campaignName));

        this.dtOptionsCampaign = this.buildOptions(rows, [
          { title: 'Contact Category', data: 'campaignName' },
          { title: 'Background Check', data: 'backgroundCheck', render: (d: string) => this.escape(d) },
          { title: 'Contact Name', data: 'campaignManagerName', render: (d: string) => this.escape(d) },
          { title: 'Contact Email', data: 'campaignManagerEmail', render: (d: string) => this.mailto(d) }
        ]);
        this.dtOptionsCommission = this.buildOptions(rows, [
          { title: 'Contact Category', data: 'campaignName' },
          { title: 'Background Check', data: 'backgroundCheck', render: (d: string) => this.escape(d) },
          { title: 'Contact Name', data: 'commissionsAnalystName', render: (d: string) => this.escape(d) },
          { title: 'Contact Email', data: 'commissionsAnalyst1Email', render: (d: string) => this.mailto(d) }
        ]);
        this.campaignReady = true;
      });
  }

  filterColumn(dir: DataTableDirective, column: number, value: string | null): void {
    if (!dir?.dtInstance) return;
    dir.dtInstance.then((dt: any) => {
      const term = value ? `^${this.escapeRegex(value)}$` : '';
      dt.column(column).search(term, true, false).draw();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildOptions(data: any[], columns: any[]): any {
    return {
      pagingType: 'full_numbers',
      pageLength: 10,
      lengthMenu: [10, 25, 50, 100],
      data,
      columns,
      dom: 'lfrtip',
      responsive: true,
      destroy: true
    };
  }

  private distinct(values: string[]): string[] {
    return Array.from(new Set(values.filter(v => !!v))).sort((a, b) => a.localeCompare(b));
  }

  private mailto(email: string): string {
    if (!email) return '';
    const safe = this.escape(email);
    return `<a href="mailto:${safe}">${safe}</a>`;
  }

  private escape(value: string): string {
    return (value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
